import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { BookOpen, MagnifyingGlass, Lightning, Wallet, Motorcycle, ShoppingCart, Path, CaretRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = { basics: Lightning, leveling: Lightning, garage: Motorcycle, marketplace: ShoppingCart, wallet: Wallet, riding: Path };

export default function HelpCenter() {
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);

    useEffect(() => {
        fetch(`${API}/help/articles`).then(r => r.json()).then(setArticles).catch(() => {});
        fetch(`${API}/help/categories`).then(r => r.json()).then(setCategories).catch(() => {});
    }, []);

    const filtered = articles.filter(a => {
        if (activeCategory && a.category !== activeCategory) return false;
        if (search && !a.title.toLowerCase().includes(search.toLowerCase()) && !a.content.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-electric-blue text-glow-blue">SUPPORT</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display">Help Center</h1>
                </motion.div>

                <div className="relative mb-6">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search help articles..." className="pl-10" data-testid="help-search" />
                </div>

                {/* Categories */}
                <div className="flex gap-2 overflow-x-auto pb-3 mb-6 no-scrollbar">
                    <button onClick={() => setActiveCategory(null)} className={`shrink-0 px-4 py-2 rounded-full text-xs font-display transition-all ${!activeCategory ? 'bg-primary text-black' : 'bg-white/5 text-muted-foreground hover:bg-white/10'}`}>All</button>
                    {categories.map(c => {
                        const Icon = CATEGORY_ICONS[c.id] || BookOpen;
                        return (
                            <button key={c.id} onClick={() => setActiveCategory(c.id)}
                                className={`shrink-0 px-4 py-2 rounded-full text-xs font-display transition-all flex items-center gap-1.5 ${activeCategory === c.id ? 'bg-primary text-black' : 'bg-white/5 text-muted-foreground hover:bg-white/10'}`}>
                                <Icon size={12} /> {c.label} ({c.count})
                            </button>
                        );
                    })}
                </div>

                {/* Article Detail */}
                {selected ? (
                    <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                        <button onClick={() => setSelected(null)} className="text-xs text-primary mb-4 font-display hover:underline">← Back to articles</button>
                        <Card className="glass-card">
                            <CardContent className="p-6">
                                <Badge className="bg-primary/10 text-primary border-primary/20 text-xs mb-3">{selected.category}</Badge>
                                <h2 className="text-xl font-bold font-display mb-4">{selected.title}</h2>
                                <p className="text-muted-foreground leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{selected.content}</p>
                            </CardContent>
                        </Card>
                    </motion.div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((article, i) => {
                            const Icon = CATEGORY_ICONS[article.category] || BookOpen;
                            return (
                                <motion.div key={article.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                                    <Card className="glass-card cursor-pointer hover:border-primary/20" onClick={() => setSelected(article)}>
                                        <CardContent className="p-4 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                                <Icon size={18} className="text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-sm truncate">{article.title}</p>
                                                <p className="text-xs text-muted-foreground truncate">{article.content.substring(0, 80)}...</p>
                                            </div>
                                            <CaretRight size={16} className="text-muted-foreground shrink-0" />
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                        {filtered.length === 0 && (
                            <Card className="glass-card text-center py-12">
                                <BookOpen size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">No articles found</p>
                            </Card>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
